import ModalLayout from "@/components/layout/ModalLayout";
import { Button } from "@/components/ui/Button";
import SelectBox from "@/components/ui/SelectBox";
import MultiSelectBox from "@/components/ui/MultiSelectBox";
import SelectedTags from "@/components/ui/SelectedTags";
import { RadioBtn } from "@/components/ui/RadioBtn";
import ModalHeader from "@/components/ui/ModalHeader";
import { useState, useMemo } from "react";
import { useUserActiveList } from "@/hooks/member/useMember";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import { useCopyPermission } from "@/hooks/menu/useMenu";
import { toast } from "@/lib/toast";

export interface WorkPsermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "copy" | "";
  isActiveFilter: string; // 현재 필터 상태 전달
}

export default function WorkPsermissionsModal({ isOpen, onClose, mode, isActiveFilter }: WorkPsermissionsModalProps) {
  const [selectedUser, setSelectedUser] = useState("");
  const [companyPermissionType, setCompanyPermissionType] = useState("all");
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedTargetUsers, setSelectedTargetUsers] = useState<string[]>([]);

  // 권한 복사 mutation
  const copyPermissionMutation = useCopyPermission();

  // isActive 파라미터 계산: "all" -> null, "active" -> true, "inactive" -> false
  const isActiveParam = isActiveFilter === "all" ? null : isActiveFilter === "active" ? true : false;

  // 모달용 전체 사용자 목록 조회 (size 1000)
  const { data: allUserList } = useUserActiveList({
    page: 0,
    size: 1000,
    search: "",
    sort: "id,desc",
    isActive: isActiveParam,
    name: "",
  });

  const userList = allUserList?.content || [];

  // 회사 목록 조회
  const { data: companyList } = useCodeListByCompanyCode();

  // 선택된 사용자의 회사 목록
  const selectedUserData = useMemo(() => {
    return userList.find(user => user.name === selectedUser);
  }, [userList, selectedUser]);

  // 선택된 사용자의 회사 옵션 생성
  const userCompanyOptions = useMemo(() => {
    if (!selectedUserData || !selectedUserData.companyId) {
      return [{ value: "", label: "선택" }];
    }

    const companyIds = String(selectedUserData.companyId)
      .split(",")
      .map(id => Number(id.trim()))
      .filter(id => !isNaN(id));

    const companyOptions = companyIds.map(companyId => {
      const company = companyList?.find(comp => comp.codeId === companyId);
      return {
        value: String(companyId),
        label: company ? company.name : `회사 ${companyId}`,
      };
    });

    return [{ value: "", label: "선택" }, ...companyOptions];
  }, [selectedUserData, companyList]);

  // 회사 선택/해제 핸들러
  const handleCompanyToggle = (companyId: string) => {
    setSelectedCompanies(prev =>
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  };

  // 회사 제거 핸들러
  const handleCompanyRemove = (companyId: string) => {
    setSelectedCompanies(prev => prev.filter(id => id !== companyId));
  };

  // 사용자 제거 핸들러
  const handleUserRemove = (userId: string) => {
    setSelectedTargetUsers(prev => prev.filter(id => id !== userId));
  };

  // 권한 적용할 사용자 목록 (권한 가져올 사용자 제외)
  const targetUserOptions = useMemo(() => {
    return userList
      .filter(user => user.name !== selectedUser) // 권한 가져올 사용자 제외
      .map(user => ({
        value: user.name,
        label: `${user.name}`,
      }));
  }, [userList, selectedUser]);

  // 선택된 사용자 옵션 생성
  const selectedUserOptions = useMemo(() => {
    return userList
      .filter(user => selectedTargetUsers.includes(user.name))
      .map(user => ({
        value: user.name,
        label: `${user.name}`,
      }));
  }, [userList, selectedTargetUsers]);

  const handleCopy = () => {
    // 유효성 검사
    if (!selectedUser) {
      toast.warning("권한 가져올 사용자를 선택해주세요.");
      return;
    }

    if (selectedTargetUsers.length === 0) {
      toast.warning("권한 적용할 사용자를 선택해주세요.");
      return;
    }

    if (companyPermissionType === "individual" && selectedCompanies.length === 0) {
      toast.warning("개별 회사를 선택해주세요.");
      return;
    }

    // 권한 가져올 사용자의 모든 회사 ID 추출
    const sourceUserCompanyIds = selectedUserData?.companyId
      ? String(selectedUserData.companyId)
          .split(",")
          .map(id => id.trim())
          .filter(id => id !== "")
      : [];

    // API 요청 데이터 생성
    const requestData = {
      sourceUserName: selectedUser,
      targetUserNames: selectedTargetUsers,
      copyCompany: true,
      selectedCompanyIds:
        companyPermissionType === "all"
          ? sourceUserCompanyIds // 전체 선택 시 권한 가져올 사용자의 모든 회사 ID
          : selectedCompanies, // 개별 선택 시 선택된 회사들
      mode: "OVERWRITE" as const,
    };
    console.log(requestData);
    // 권한 복사 실행
    copyPermissionMutation.mutate(requestData, {
      onSuccess: () => {
        onClose();
        // 상태 초기화
        setSelectedUser("");
        setCompanyPermissionType("all");
        setSelectedCompanies([]);
        setSelectedTargetUsers([]);
      },
    });
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} title="권한 복사" zIndexClass="z-9999">
      <div className="flex gap-6 h-[400px]">
        {/* 왼쪽 섹션: 복사 기준 */}
        <div className="flex-1 flex flex-col">
          <ModalHeader title="복사 기준" />

          <div className="flex flex-col gap-4">
            {/* 권한 가져올 사용자 */}
            <div>
              <label className="block text-base font-normal text-[#414651] mb-2">권한 가져올 사용자</label>
              <SelectBox
                value={selectedUser}
                onChange={(value: string) => setSelectedUser(value)}
                options={[
                  { value: "", label: "선택" },
                  ...userList.map(user => ({
                    value: user.name,
                    label: `${user.name}`,
                  })),
                ]}
                placeholder="선택"
              />
            </div>

            {/* 복사할 회사 권한 */}
            <div>
              <label className="block text-base font-normal text-[#414651] mb-2">복사할 회사 권한</label>
              <div className="flex gap-4 flex-col">
                <RadioBtn
                  name="companyPermission"
                  value="all"
                  checked={companyPermissionType === "all"}
                  onChange={() => {
                    setCompanyPermissionType("all");
                    setSelectedCompanies([]);
                  }}
                  label="전체"
                  style="green"
                />
                <RadioBtn
                  name="companyPermission"
                  value="individual"
                  checked={companyPermissionType === "individual"}
                  onChange={() => setCompanyPermissionType("individual")}
                  label="개별"
                  style="green"
                />
              </div>
            </div>

            {/* 복사할 회사 선택 */}
            <div>
              <MultiSelectBox
                label="복사할 회사 선택"
                values={selectedCompanies}
                onChange={setSelectedCompanies}
                options={userCompanyOptions.filter(opt => opt.value !== "")}
                placeholder="선택"
                disabled={companyPermissionType === "all"}
              />
              <SelectedTags
                label="선택된 회사:"
                selectedItems={userCompanyOptions.filter(opt => selectedCompanies.includes(opt.value))}
                onRemove={handleCompanyRemove}
                className="mt-2"
              />
            </div>
          </div>
        </div>

        {/* 오른쪽 섹션: 적용 대상 */}
        <div className="flex-1 flex flex-col">
          <ModalHeader title="적용 대상" />

          <div className="flex flex-col gap-4">
            {/* 권한 적용할 사용자 */}
            <div>
              <MultiSelectBox
                label="권한 적용할 사용자 (다중 선택 가능)"
                values={selectedTargetUsers}
                onChange={setSelectedTargetUsers}
                options={targetUserOptions}
                placeholder="선택"
              />
              <SelectedTags
                label="선택된 사용자:"
                selectedItems={selectedUserOptions}
                onRemove={handleUserRemove}
                className="mt-2"
              />
            </div>
          </div>
        </div>
      </div>

      {/* 하단 버튼 */}
      <div className="flex justify-between mt-6">
        <Button variant="reset" onClick={onClose} className="">
          취소
        </Button>
        <Button variant="primary" onClick={handleCopy} className="">
          복사
        </Button>
      </div>
    </ModalLayout>
  );
}
